/**
 * @file snapshot.ts
 * @description Снимок страницы или места для версии (phase19.16 Р3а «черновик
 * и версии страниц и мест»; SB126; db-design-r3a §2, §3.1, §5).
 *
 * Снимок — то, что лежит в колонках неизменяемой `SitePageRevision`
 * (опубликованное), и то, во что сервер собирает черновик (колонки
 * `SitePage` + строки `SitePageBlock`) перед сравнением с опубликованным.
 * Состав по §3.1: `{ slug, title, pageKind, seoTitle, seoDescription,
 * noIndex, background, design, sections }`:
 *   - `design` — сырой назначенный дизайн `{ designId, schema, theme,
 *     brandVisual }` без имени (переименование дизайна — не правка страницы);
 *     витрина читает его `readSiteDesign` со шрифтами магазина (§5);
 *   - `sectionContext` (плотность и шкала заголовков на момент публикации) в
 *     снимок не входит: это колонка-страховка для будущих подъёмов формата;
 *   - блоки прежних типов (не SECTION) в снимок не входят;
 *   - `updatedAt`, кто правил, `sortOrder` в снимок не входят — только
 *     порядок в массиве; `key` блока = id строки `SitePageBlock` (нужен
 *     возврату версии и «Отменить правки», db-design-r3a §8 тесты).
 *
 * Черновик и версия читаются одним путём (`readSitePageSections`,
 * `readPageBackground`, `readSitePageDesign`) — поэтому отпечатки совпадают
 * байт в байт, а повторное чтение снимка его не меняет (§3.1, строка
 * «правок после переноса: 0» команды site:baseline-pages).
 *
 * Хэш отпечатка считает сервер (node:crypto, `sha256(sitePageCanonicalJson)`):
 * пакет используется и в браузере, поэтому здесь только канонический JSON.
 *
 * «SEO-техника» v0.19.65 (SB146; db-design-seo-tech §1.3): хлебные крошки
 * страницы `breadcrumbs` (`INHERIT | SHOW | HIDE`) — тем же путём, что
 * `noIndex`. У места всегда `INHERIT`. В нормализованный снимок ключ входит
 * только при значении ≠ `INHERIT`: канонический JSON и отпечатки всех
 * существующих страниц не меняются, и вкладка кабинета, открытая до
 * выкладки, не получает ложный 409 при публикации.
 *
 * Версия формата: отдельной колонки у версии нет (§2). Формат секций
 * версионирует `payloadVersion` каждого блока — старый блок поднимается при
 * чтении (uiux-r3 §7), контекст подъёма — колонка `sectionContext`.
 */

import type { SectionContext } from "../site-section/contexts.js";
import { collectSectionMediaIds } from "../site-section/media-ids.js";
import {
  readSnapshotSectionList,
  type SnapshotSection,
  type SnapshotSectionIssue,
} from "../site-section/snapshot-sections.js";
import type { SectionLegacyContext } from "../site-section/upgrade-v4.js";
import { readSiteDesign, siteDesignMediaIds } from "../site-theme/design.js";
import { SITE_SPACING_DENSITIES, type SiteSpacingDensityV1 } from "../site-theme/legacy-v1.js";
import { SITE_LIMITS } from "../site-theme/limits.js";
import {
  readPageBackground,
  sitePageBackgroundMediaIds,
  type SitePageBackground,
} from "../site-theme/page-background.js";
import { canonicalJson } from "../site-theme/snapshot.js";

// ── Типы ──

/**
 * Вид страницы в версии (CHECK `pageKind` у `SitePageRevision`): шаблон
 * товара (`TEMPLATE`) публикуется с видом сайта и сюда не попадает (§1.2).
 */
export const SITE_PAGE_SNAPSHOT_KINDS = ["SYSTEM_HOME", "CUSTOM", "SLOT"] as const;
export type SitePageSnapshotKind = (typeof SITE_PAGE_SNAPSHOT_KINDS)[number];

export function isSitePageSnapshotKind(value: unknown): value is SitePageSnapshotKind {
  return typeof value === "string" && (SITE_PAGE_SNAPSHOT_KINDS as readonly string[]).includes(value);
}

/**
 * Хлебные крошки страницы (SB146; Prisma-enum `SitePageBreadcrumbs`):
 * «как на сайте» (переключатель «Весь сайт → Навигация»), «показывать»,
 * «скрыть». Решение «показывать ли» — `showBreadcrumbs` (site-seo/breadcrumbs.ts).
 */
export const SITE_PAGE_BREADCRUMBS = ["INHERIT", "SHOW", "HIDE"] as const;
export type SitePageBreadcrumbs = (typeof SITE_PAGE_BREADCRUMBS)[number];

export function isSitePageBreadcrumbs(value: unknown): value is SitePageBreadcrumbs {
  return typeof value === "string" && (SITE_PAGE_BREADCRUMBS as readonly string[]).includes(value);
}

/**
 * Значение крошек из колонки: нет, NULL или чужое — `INHERIT` (CHECK в базе
 * чужого не пропустит; «как на сайте» — безопасная деградация).
 */
export function readSitePageBreadcrumbs(raw: unknown): SitePageBreadcrumbs {
  return isSitePageBreadcrumbs(raw) ? raw : "INHERIT";
}

/** Блок страницы в снимке — тот же формат, что у шаблона товара (`SnapshotSection`). */
export type SitePageSection = SnapshotSection;

/**
 * Снимок назначенного дизайна страницы (форма строки `SiteDesign` без имени).
 * `theme` и `brandVisual` — как лежат в базе: подъём версии оформления
 * делает `readSiteDesign` при показе, а снимок остаётся сырым, чтобы черновик
 * и версия совпадали байт в байт (§3.1).
 */
export interface SitePageDesignSnapshot {
  designId: string;
  schema: number;
  theme: unknown;
  brandVisual: unknown;
}

/** Снимок страницы или места (§3.1). Порядок ключей не важен — сравнение по каноническому JSON. */
export interface SitePageSnapshot {
  slug: string;
  title: string;
  pageKind: SitePageSnapshotKind;
  seoTitle: string | null;
  seoDescription: string | null;
  noIndex: boolean;
  /**
   * Хлебные крошки (SB146). Нет ключа = `INHERIT`: так выглядит
   * нормализованный снимок (`normalizeSitePageSnapshot` убирает `INHERIT`).
   * Чтение версии и сборка черновика ставят ключ всегда.
   */
  breadcrumbs?: SitePageBreadcrumbs;
  /** Фон страницы; null — «как у сайта». У места всегда null (§1.2). */
  background: SitePageBackground | null;
  /** Назначенный дизайн; null — «как у сайта». У места всегда null (§1.2). */
  design: SitePageDesignSnapshot | null;
  /** Секции по порядку, включая выключенные (`isEnabled = false`). */
  sections: SitePageSection[];
}

/** Замечание чтения снимка: путь от корня (`sections.2.payload.look`, `design`) и причина. */
export type SitePageReadIssue = SnapshotSectionIssue;

/** Контекст элементов секций по виду страницы: место — «slot», остальное — «page». */
export function sitePageSectionContext(kind: SitePageSnapshotKind): Extract<SectionContext, "page" | "slot"> {
  return kind === "SLOT" ? "slot" : "page";
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDensityV1 = (value: unknown): value is SiteSpacingDensityV1 =>
  typeof value === "string" && (SITE_SPACING_DENSITIES as readonly string[]).includes(value);

// ── Чтение ──

export interface SitePageSectionsReadOptions {
  /** Где стоят секции; по умолчанию — обычная страница. */
  context?: Extract<SectionContext, "page" | "slot">;
  /** Тема, которой рисовались блоки v2/v3 (`sectionContextForBlocks` / колонка `sectionContext`). */
  legacy?: SectionLegacyContext;
  /** Снято в v4 — передавайте `legacy`; `never` ловит и распространение `...{ density }`. */
  density?: never;
}

/**
 * Секции из снимка (колонка `SitePageRevision.sections`) с подъёмом формата
 * старых блоков и починкой битых — обобщение `readProductTemplateSnapshot`
 * (§3.1). NULL — пустой список без замечаний (пустое место); не массив —
 * пустой список с замечанием. Никогда не бросает. Лимит —
 * `SITE_LIMITS.sectionsPerPage` (тот же у страницы и места, SB64).
 */
export function readSitePageSections(
  raw: unknown,
  replaced: SitePageReadIssue[] = [],
  opts: SitePageSectionsReadOptions = {},
): SitePageSection[] {
  if (raw === null || raw === undefined) return [];
  if (!Array.isArray(raw)) {
    replaced.push({ path: "sections", message: "Секции версии не прочитаны — страница без блоков" });
    return [];
  }
  return readSnapshotSectionList(
    raw,
    {
      context: opts.context ?? "page",
      limit: SITE_LIMITS.sectionsPerPage,
      ...(opts.legacy !== undefined ? { legacy: opts.legacy } : {}),
    },
    replaced,
  );
}

/**
 * Снимок дизайна из колонки версии: проверяется только форма (id, номер
 * версии оформления); содержимое не трогается. Битое — «как у сайта» с
 * замечанием: витрина не падает.
 */
export function readSitePageDesign(raw: unknown, replaced: SitePageReadIssue[] = []): SitePageDesignSnapshot | null {
  if (raw === null || raw === undefined) return null;
  if (
    isPlainObject(raw) &&
    typeof raw["designId"] === "string" &&
    raw["designId"].length > 0 &&
    typeof raw["schema"] === "number" &&
    Number.isInteger(raw["schema"]) &&
    raw["schema"] >= 1
  ) {
    return { designId: raw["designId"], schema: raw["schema"], theme: raw["theme"] ?? null, brandVisual: raw["brandVisual"] ?? null };
  }
  replaced.push({ path: "design", message: "Снимок дизайна страницы не прочитан — оформление как у сайта" });
  return null;
}

/**
 * Контекст подъёма из колонки `SitePageRevision.sectionContext`
 * (`{ density, headingScale }`); нет или битое — undefined (блоки текущей
 * версии контекста не требуют, старые поднимутся по умолчаниям с заметкой).
 */
export function readSitePageSectionContext(raw: unknown): SectionLegacyContext | undefined {
  if (!isPlainObject(raw)) return undefined;
  const ctx: SectionLegacyContext = {};
  const density = raw["density"];
  if (typeof density === "number" && Number.isFinite(density)) ctx.density = density;
  else if (isDensityV1(density)) ctx.density = density;
  const headingScale = raw["headingScale"];
  if (typeof headingScale === "number" && Number.isFinite(headingScale)) ctx.headingScale = headingScale;
  return ctx.density === undefined && ctx.headingScale === undefined ? undefined : ctx;
}

/** Колонки версии (`SitePageRevision`) — как их отдаёт Prisma. */
export interface SitePageRevisionColumns {
  slug: string;
  title: string;
  /** Prisma-enum `SitePageKind`; `TEMPLATE` и чужое — нарушение инварианта (CHECK). */
  pageKind: string;
  seoTitle: string | null;
  seoDescription: string | null;
  noIndex: boolean;
  /**
   * «SEO-техника» (SB146): Prisma-enum `SitePageBreadcrumbs`. Необязательно на
   * переходный период (select'ы apps/api до волны 2): нет — `INHERIT`.
   */
  breadcrumbs?: string | null;
  background: unknown;
  design: unknown;
  sections: unknown;
  /** `{ density, headingScale }` на момент публикации; нет — подъём по умолчаниям. */
  sectionContext?: unknown;
}

export interface SitePageSnapshotReadResult {
  snapshot: SitePageSnapshot;
  replaced: SitePageReadIssue[];
}

/**
 * Чтение версии (витрина, «Посмотреть», возврат, «Отменить правки»).
 * Вид страницы вне `SITE_PAGE_SNAPSHOT_KINDS` — ошибка (fail fast: CHECK в
 * базе это исключает); остальное деградирует с замечанием.
 */
export function readSitePageSnapshot(
  columns: SitePageRevisionColumns,
  opts: { legacy?: SectionLegacyContext } = {},
): SitePageSnapshotReadResult {
  if (!isSitePageSnapshotKind(columns.pageKind)) {
    throw new Error(`site_page_snapshot_kind_invalid: ${columns.pageKind}`);
  }
  const replaced: SitePageReadIssue[] = [];
  const bg = readPageBackground(columns.background);
  if (bg.replaced) replaced.push({ path: "background", message: "Фон страницы не прочитан — как у сайта" });
  const legacy = opts.legacy ?? readSitePageSectionContext(columns.sectionContext);
  return {
    snapshot: {
      slug: columns.slug,
      title: columns.title,
      pageKind: columns.pageKind,
      seoTitle: columns.seoTitle,
      seoDescription: columns.seoDescription,
      noIndex: columns.noIndex,
      // У места только INHERIT (CHECK SitePageRevision_breadcrumbs_check) — страхуем и при чтении.
      breadcrumbs: columns.pageKind === "SLOT" ? "INHERIT" : readSitePageBreadcrumbs(columns.breadcrumbs),
      background: bg.background,
      design: readSitePageDesign(columns.design, replaced),
      sections: readSitePageSections(columns.sections, replaced, {
        context: sitePageSectionContext(columns.pageKind),
        ...(legacy !== undefined ? { legacy } : {}),
      }),
    },
    replaced,
  };
}

// ── Черновик → снимок ──

/** Колонки страницы-черновика (`SitePage`). */
export interface SitePageDraftColumns {
  slug: string;
  title: string;
  /** `SitePage.kind`; `TEMPLATE` — ошибка (шаблон товара публикуется с видом сайта). */
  kind: string;
  seoTitle: string | null;
  seoDescription: string | null;
  noIndex: boolean;
  /** «SEO-техника» (SB146): `SitePage.breadcrumbs`; нет — `INHERIT` (переходный период). */
  breadcrumbs?: string | null;
  background: unknown;
}

/** Назначенный дизайн (строка `SiteDesign`; имя и даты не нужны). */
export interface SitePageDraftDesign {
  id: string;
  schema: number;
  theme: unknown;
  brandVisual: unknown;
}

/** Строка блока черновика (`SitePageBlock`). */
export interface SitePageDraftBlock {
  id: string;
  /** `SiteBlockType`; не SECTION — блок прежнего типа, в снимок не входит. */
  type: string;
  sortOrder: number;
  isEnabled: boolean;
  payload: unknown;
  payloadVersion: number | null;
  appearance: unknown;
}

/**
 * Снимок черновика из колонок, дизайна и строк блоков — чистая часть
 * `loadPageDraftSnapshot` (api читает базу, здесь — сборка). Блоки SECTION
 * по `sortOrder`, при равенстве — по id (порядок в базе не уникален, а
 * отпечаток должен быть воспроизводимым); выключенные входят. Блоки
 * проходят тот же путь, что версия при чтении: прочитанный черновик и
 * прочитанная версия равны, если равны их источники.
 */
export function sitePageSnapshotFromDraft(
  page: SitePageDraftColumns,
  design: SitePageDraftDesign | null,
  blocks: readonly SitePageDraftBlock[],
  opts: { legacy?: SectionLegacyContext } = {},
  replaced: SitePageReadIssue[] = [],
): SitePageSnapshot {
  if (!isSitePageSnapshotKind(page.kind)) {
    throw new Error(`site_page_snapshot_kind_invalid: ${page.kind}`);
  }
  const isSlot = page.kind === "SLOT";
  const ordered = blocks
    .filter((b) => b.type === "SECTION")
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const rawSections = ordered.map((b) => ({
    key: b.id,
    isEnabled: b.isEnabled,
    payloadVersion: b.payloadVersion ?? 1,
    payload: b.payload,
    appearance: b.appearance,
  }));
  const bg = isSlot ? { background: null, replaced: false } : readPageBackground(page.background);
  if (bg.replaced) replaced.push({ path: "background", message: "Фон страницы не прочитан — как у сайта" });
  return {
    slug: page.slug,
    title: page.title,
    pageKind: page.kind,
    seoTitle: page.seoTitle,
    seoDescription: page.seoDescription,
    // Место не индексируется само по себе (§1.2).
    noIndex: isSlot ? true : page.noIndex,
    // Место крошек не показывает — у него всегда «как на сайте» (CHECK SitePage_breadcrumbs_check).
    breadcrumbs: isSlot ? "INHERIT" : readSitePageBreadcrumbs(page.breadcrumbs),
    background: bg.background,
    design:
      isSlot || design === null
        ? null
        : { designId: design.id, schema: design.schema, theme: design.theme ?? null, brandVisual: design.brandVisual ?? null },
    sections: readSitePageSections(rawSections, replaced, {
      context: sitePageSectionContext(page.kind),
      ...(opts.legacy !== undefined ? { legacy: opts.legacy } : {}),
    }),
  };
}

// ── Канонический JSON (основа отпечатка) ──

/**
 * Снимок только из известных ключей (§3.1): лишнее поле в объекте (имя
 * дизайна, `updatedAt` из небрежного select) не должно менять отпечаток;
 * `undefined` и пустая строка в SEO-полях приравниваются к null.
 */
export function normalizeSitePageSnapshot(s: SitePageSnapshot): SitePageSnapshot {
  const breadcrumbs = readSitePageBreadcrumbs(s.breadcrumbs);
  return {
    slug: s.slug,
    title: s.title,
    pageKind: s.pageKind,
    // Пустая строка = null: витрина их не различает (pageTitle/pageDescription), иначе у старых
    // страниц с "" после переноса появилась бы ложная «1 правка».
    seoTitle: s.seoTitle || null,
    seoDescription: s.seoDescription || null,
    noIndex: s.noIndex,
    // SB146: `INHERIT` в снимок не пишется — отпечатки страниц до v0.19.65 прежние байт в байт.
    ...(breadcrumbs === "INHERIT" ? {} : { breadcrumbs }),
    background: s.background ?? null,
    design: s.design
      ? { designId: s.design.designId, schema: s.design.schema, theme: s.design.theme ?? null, brandVisual: s.design.brandVisual ?? null }
      : null,
    sections: s.sections.map((sec) => ({
      key: sec.key,
      isEnabled: sec.isEnabled,
      payloadVersion: sec.payloadVersion,
      payload: sec.payload,
      appearance: sec.appearance ?? null,
    })),
  };
}

/**
 * Канонический JSON снимка: ключи по алфавиту на любой глубине, порядок
 * блоков сохраняется (он значим). Отпечаток = `sha256` этой строки (api).
 */
export function sitePageCanonicalJson(snapshot: SitePageSnapshot): string {
  return canonicalJson(normalizeSitePageSnapshot(snapshot));
}

/** Совпадают ли два снимка (то же, что равенство отпечатков). */
export function sitePageSnapshotsEqual(a: SitePageSnapshot, b: SitePageSnapshot): boolean {
  return sitePageCanonicalJson(a) === sitePageCanonicalJson(b);
}

// ── Медиа ──

/**
 * Все медиа снимка без повторов, по алфавиту (`SitePageRevision.mediaIds`,
 * «где используется»): секции, фон страницы, дизайн (фон и рисунки темы,
 * логотипы и рисунок подвала). Данные дизайна новее кода — ошибка
 * `readSiteDesign` пробрасывается (как у витрины).
 */
export function sitePageMediaIds(snapshot: SitePageSnapshot): string[] {
  const ids = new Set<string>();
  for (const sec of snapshot.sections) collectSectionMediaIds(sec.payload).forEach((id) => ids.add(id));
  sitePageBackgroundMediaIds(snapshot.background).forEach((id) => ids.add(id));
  if (snapshot.design) {
    // Шрифты на медиа не влияют — реестр магазина не нужен.
    siteDesignMediaIds(readSiteDesign(snapshot.design)).forEach((id) => ids.add(id));
  }
  return [...ids].sort();
}
